import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { saveAs } from 'file-saver';
import { first, isString } from 'lodash';
import { File as FileModel } from 'src/app/classes/models/file';
import { ExcelFileType } from 'src/app/interfaces/excel-file-type';
import { ImageFileType } from 'src/app/interfaces/image-file-type';
import { PDFFileType } from 'src/app/interfaces/pdf-file-type';
import { WordFileType } from 'src/app/interfaces/word-file-type';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-file-upload-file-display',
  templateUrl: './file-upload-file-display.component.html',
  styleUrls: ['./file-upload-file-display.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
// This component both uploads files and displays a scrollable list of uploaded files (as a card-column)
export class FileUploadFileDisplayComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private UtilityService: UtilityService
  ) {}

  // This is used to display a list of previously uploaded files as a card column.
  @Input() Files: FileModel[] = [];

  @Input() selectFileButtonText = 'SELECT';
  @Input() removeFileButtonText = 'DELETE';

  @Input() hideSelectFileButton: boolean = false;
  @Input() hideRemoveFileButton: boolean = false;
  @Input() showViewFileButton: boolean = false;

  @Input() showFileType: boolean = true;

  // Determines whether file information should be displayed as file name, file size, file type (default)
  // or just file name (minimal).
  @Input() fileInfoDisplayType: 'default' | 'minimal' = 'default';

  // Layout property determines if the upload/file selection are side-by-side (horizontal) or top-to-bottom (vertical)
  @Input() layout: 'horizontal' | 'vertical' = 'horizontal';

  // Used to display files as a row, rather than a column.
  @Input() displayFilesAsRow: boolean = false;
  // This is only used for COI input.
  @Input() displayNoScanButton: boolean = false;
  // When enabled, only displays uploaded files. Does not allow adding/removing files.
  @Input() displayOnly: boolean = false;

  // Prevent file upload
  @Input() disableFileUpload: boolean = false;

  @Output() FileEvent = new EventEmitter<{
    data: File;
    type: null | 'No Scan';
  }>();
  @Output() selectFileEvent = new EventEmitter<{
    File: FileModel;
    index: number;
  }>();
  @Output() removeFileEvent = new EventEmitter<{
    File: FileModel;
    index: number;
  }>();
  @Output() viewFileEvent = new EventEmitter<FileModel>();
  @Output() downloadFileEvent = new EventEmitter<FileModel>();

  @ViewChild('fileUploadInput')
  fileUploadInputElement?: ElementRef<HTMLInputElement>;
  @ViewChild('fileUploadInputNoScan')
  fileUploadInputElementNoScan?: ElementRef<HTMLInputElement>;

  ExcelFileTypes: ExcelFileType[] = [
    'xlsx',
    'xlsb',
    'xltx',
    'xls',
    'xlt',
    'xla',
    'csv'
  ];
  ImageFileTypes: ImageFileType[] = [
    'jpeg',
    'jpg',
    'tiff',
    'gif',
    'bmp',
    'png',
    'bat',
    'svg'
  ];
  PdfFileTypes: PDFFileType[] = ['pdf'];
  WordFileTypes: WordFileType[] = ['doc', 'docm', 'docx', 'dotx', 'dotm'];

  FileTypes: {
    xlsx: 'Excel';
    xlsb: 'Excel';
    xltx: 'Excel';
    xls: 'Excel';
    xlt: 'Excel';
    xla: 'Excel';
    csv: 'Excel';

    jpeg: 'Image';
    jpg: 'Image';
    tiff: 'Image';
    gif: 'Image';
    bmp: 'Image';
    png: 'Image';
    bat: 'Image';
    svg: 'Image';

    pdf: 'PDF';

    doc: 'Word';
    docm: 'Word';
    docx: 'Word';
    dotx: 'Word';
    dotm: 'Word';
  } = {
    xlsx: 'Excel',
    xlsb: 'Excel',
    xltx: 'Excel',
    xls: 'Excel',
    xlt: 'Excel',
    xla: 'Excel',
    csv: 'Excel',

    jpeg: 'Image',
    jpg: 'Image',
    tiff: 'Image',
    gif: 'Image',
    bmp: 'Image',
    png: 'Image',
    bat: 'Image',
    svg: 'Image',

    pdf: 'PDF',

    doc: 'Word',
    docm: 'Word',
    docx: 'Word',
    dotx: 'Word',
    dotm: 'Word'
  };

  getFriendlyFileTypeName(File: FileModel) {
    const { filename } = File;

    if (!filename) {
      return 'Missing file name.';
    }

    const fileType = filename.split('.').pop();

    if (!fileType) {
      return 'Missing file type.';
    }

    const friendlyFileTypeName =
      this.FileTypes[
        fileType as WordFileType | PDFFileType | ExcelFileType | ImageFileType
      ];

    // Not every file extension will be friendly.
    if (!friendlyFileTypeName) {
      return fileType;
    }

    return friendlyFileTypeName;
  }

  generateFileDataUri({
    fileType,
    fileData
  }: {
    fileType: string;
    fileData: string;
  }): string {
    // If the file data is already a base64 encoded uri, return the uri.
    if (/data:[\w/]*;base64,/.test(fileData)) {
      return fileData;
    }
    return `data:${fileType};base64,${fileData}`;
  }

  downloadFile(File: FileModel) {
    const { filetype, filename, FileContent } = File;
    if (filetype && FileContent && filename) {
      const { filecontents } = FileContent;
      if (!filecontents) {
        throw this.ToastService.addErrorToast(
          'Error',
          'File is missing data.',
          { returnError: true }
        );
      }
      const dataURI = this.generateFileDataUri({
        fileType: filetype,
        fileData: filecontents
      });

      saveAs(dataURI, filename);
    } else {
      this.downloadFileEvent.emit(new FileModel(File));
    }
  }

  emitFileEvent(files: FileList, type: null | 'No Scan' = null) {
    if (files.length > 1) {
      this.ToastService.addErrorToast(
        'Error',
        'Cannot upload more than one file at once.'
      );
      throw new Error('Cannot upload more than one file at once.');
    }
    const file = first(files);
    if (file) {
      this.FileEvent.emit({ data: file, type });
    }
  }

  // This event should be overridden if extended into a new component.
  // Used to handle a file drop event.
  fileDrop(dropEvent: DragEvent) {
    dropEvent.preventDefault();

    if (dropEvent.dataTransfer) {
      const files = dropEvent.dataTransfer.files;
      this.emitFileEvent(files);
    }
  }

  fileDragOver(dragOverEvent: Event) {
    dragOverEvent.preventDefault();
  }

  // This triggers the file selection window to pop up.
  uploadFile(type: null | 'No Scan' = null) {
    if (this.fileUploadInputElement && !type) {
      this.fileUploadInputElement.nativeElement.click();
    }
    if (this.fileUploadInputElementNoScan && type === 'No Scan') {
      this.fileUploadInputElementNoScan.nativeElement.click();
    }
  }

  // Handle a file selected from the file selection window
  selectedFile(File: Event, type: null | 'No Scan' = null) {
    [this.fileUploadInputElement, this.fileUploadInputElementNoScan].forEach(
      (element) => {
        if (element) {
          const { nativeElement } = element;
          const { files } = nativeElement;
          if (files) {
            this.emitFileEvent(files, type);
          }
          nativeElement.value = '';
        }
      }
    );
  }

  normalizeFileSizeText(fileSize: string | number): string {
    const fileSizeInteger = isString(fileSize)
      ? parseInt(fileSize, 10)
      : fileSize;
    if (fileSizeInteger >= 1000 && fileSizeInteger < 1000000) {
      return `${Math.round((fileSizeInteger / 1000) * 10) / 10} KB`;
    } else if (fileSizeInteger >= 1000000) {
      return `${Math.round((fileSizeInteger / 1000000) * 10) / 10} MB`;
    } else {
      return `${fileSizeInteger} Bytes`;
    }
  }

  selectFile(selectedFile: FileModel, index: number) {
    this.selectFileEvent.emit({ File: selectedFile, index });
  }

  removeFile(removedFile: FileModel, index: number) {
    this.removeFileEvent.emit({ File: new FileModel(removedFile), index });
  }

  viewData(File: FileModel, index: number) {
    const { filetype, filename, FileContent } = File;
    if (filetype && FileContent && filename) {
      const { filecontents } = FileContent;
      if (!filecontents) {
        throw this.ToastService.addErrorToast(
          'Error',
          'File is missing data.',
          { returnError: true }
        );
      }
      const ArrayBuffer =
        this.UtilityService.convertBase64StringToArrayBuffer(filecontents);
      window.open(
        URL.createObjectURL(new Blob([ArrayBuffer], { type: filetype })),
        '_blank'
      );
    } else {
      this.viewFileEvent.emit(File);
    }
  }

  // Used in the html template. Cannot use typeof in binding syntax.
  isString(value: any) {
    return isString(value);
  }

  ngOnInit(): void {}
}
